import { z } from 'zod';
import { ModalidadSchema } from './common.js';
import { EstadoSimulacionSchema } from './estados.js';

// ---------- GET /api/mis-simulaciones (§4.1) ----------

export const MiSimulacionListadaEscenarioSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  duracionMaxMin: z.number(),
});
export type MiSimulacionListadaEscenario = z.infer<typeof MiSimulacionListadaEscenarioSchema>;

export const MiSimulacionListadaAlumnoSchema = z.object({
  nombre: z.string(),
});
export type MiSimulacionListadaAlumno = z.infer<typeof MiSimulacionListadaAlumnoSchema>;

export const MiSimulacionListadaSchema = z.object({
  id: z.string(),
  estado: EstadoSimulacionSchema,
  /** ISO datetime. */
  iniciadaEn: z.string(),
  /** ISO datetime; `null` mientras la simulación no haya terminado. */
  completadaEn: z.string().nullable(),
  /** `null` mientras la simulación no haya terminado. */
  duracionSegundos: z.number().nullable(),
  modalidad: ModalidadSchema,
  escenario: MiSimulacionListadaEscenarioSchema,
  /** Sólo presente para roles distintos de ALUMNO (filtrado backend-side). */
  alumno: MiSimulacionListadaAlumnoSchema.optional(),
});
export type MiSimulacionListada = z.infer<typeof MiSimulacionListadaSchema>;

export const MisSimulacionesResponseSchema = z.array(MiSimulacionListadaSchema);
export type MisSimulacionesResponse = z.infer<typeof MisSimulacionesResponseSchema>;

// ---------- GET /api/simulacion/:id/detalle (§4.2) ----------

export const PerfPipelineSchema = z.object({
  stt_ms: z.number(),
  ia_ms: z.number(),
  tts_ms: z.number(),
  pipeline_total_ms: z.number(),
});
export type PerfPipeline = z.infer<typeof PerfPipelineSchema>;

export const TranscripcionItemSchema = z.object({
  secuencia: z.number(),
  alumno_transcripcion: z.string(),
  transcripcion: z.string(),
  /** ISO datetime. */
  ejecutado_en: z.string(),
  perf: PerfPipelineSchema,
});
export type TranscripcionItem = z.infer<typeof TranscripcionItemSchema>;

/**
 * Métricas finales. INV-04: sólo visibles para TUTOR / ADMINISTRADOR /
 * VALIDADOR_CLINICO — nunca al ALUMNO. El backend devuelve `null` en este
 * campo cuando el solicitante es alumno.
 */
export const MetricasFinalesSchema = z.object({
  trust: z.number(),
  pain: z.number(),
  alicia_progress: z.number(),
});
export type MetricasFinales = z.infer<typeof MetricasFinalesSchema>;

export const DetalleSimulacionEscenarioSchema = z.object({
  id: z.string(),
  nombre: z.string(),
});
export type DetalleSimulacionEscenario = z.infer<typeof DetalleSimulacionEscenarioSchema>;

export const DetalleSimulacionAlumnoSchema = z.object({
  nombre: z.string(),
});
export type DetalleSimulacionAlumno = z.infer<typeof DetalleSimulacionAlumnoSchema>;

export const DetalleSimulacionResponseSchema = z.object({
  id: z.string(),
  estado: EstadoSimulacionSchema,
  iniciadaEn: z.string(),
  completadaEn: z.string().nullable(),
  duracionSegundos: z.number().nullable(),
  modalidad: ModalidadSchema,
  escenario: DetalleSimulacionEscenarioSchema,
  alumno: DetalleSimulacionAlumnoSchema,
  transcripcion: z.array(TranscripcionItemSchema),
  /** R3 — siempre `null` en clientes 2D (examen físico exclusivo de VR). */
  resultado_examen_fisico: z.null(),
  /** `null` para alumno (INV-04); poblado para tutor/admin/validador. */
  metricas_finales: MetricasFinalesSchema.nullable(),
});
export type DetalleSimulacionResponse = z.infer<typeof DetalleSimulacionResponseSchema>;
